/**
 * PATH: apps/qa/src/components/AreaCard.tsx
 * PURPOSE: Field-ready area card with always-visible Camera/File/Note actions,
 *          visual state indicators, and one-tap completion toggle.
 *
 * WHY: The old design hid actions behind a "Notes & files" toggle that required
 *      expanding, finding the button, clicking, and then adding content. Field
 *      workers on construction sites need to tap Camera → snap → move on.
 *
 * FLOW:
 *   ┌───────────────────────────────────────────────────┐
 *   │ Area Name (code)          [status] [✓ complete]  │
 *   │ 📷 2  📎 1  📝 3  ← evidence badges             │
 *   │ ┌────────┐ ┌────────┐ ┌────────┐                │
 *   │ │📷 Photo│ │📎 File │ │📝 Note │  ← always vis  │
 *   │ └────────┘ └────────┘ └────────┘                │
 *   │ (inline evidence list — auto-shown when content) │
 *   └───────────────────────────────────────────────────┘
 *
 * DEPENDENCIES:
 *   - evidenceApi: CRUD for notes/files/photos
 *   - lockArea / unlockArea: Area completion toggle
 *   - @tanstack/react-query: Data fetching and cache invalidation
 */

import { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { evidenceApi } from '@/lib/api'
import type { EvidenceItem } from '@/lib/api'
import { cn } from '@/lib/utils'
import api from '@/lib/api'
import {
  Camera,
  Upload,
  MessageSquare,
  Check,
  Lock,
  Unlock,
  Loader2,
  Trash2,
  Image,
  File,
  ChevronDown,
  ChevronRight,
  Play,
} from 'lucide-react'
import { AreaCardPhotoSources, QA_PHOTO_ACCEPT } from './AreaCardPhotoSources'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface AreaCardProps {
  area: {
    id: string
    area_name: string
    area_code: string
    status: string
    is_locked?: boolean
  }
  inspectionId: string
  /** Called after lock/unlock completes so parent can refetch */
  onStatusChange?: () => void
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function AreaCard({ area, inspectionId, onStatusChange }: AreaCardProps) {
  const queryClient = useQueryClient()
  const [isAddingNote, setIsAddingNote] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showEvidence, setShowEvidence] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const noteInputRef = useRef<HTMLTextAreaElement>(null)

  const isLocked = area.status === 'locked' || area.is_locked
  const queryKey = ['qa-evidence', { area_id: area.id }]

  // ── Fetch evidence for this area (always, so we can show badges) ─────────
  const { data: evidenceData } = useQuery({
    queryKey,
    queryFn: () => evidenceApi.getEvidence({ area_id: area.id, page_size: 100 }),
  })

  const evidence: EvidenceItem[] = Array.isArray(evidenceData?.results)
    ? evidenceData.results
    : Array.isArray(evidenceData) ? evidenceData : []

  const photoCount = evidence.filter((e) => e.type === 'photo' || e.type === 'video').length
  const fileCount = evidence.filter((e) => e.type === 'document').length
  const noteCount = evidence.filter((e) => e.type === 'note').length
  const totalCount = evidence.length
  const hasContent = totalCount > 0

  // ── Mutations ────────────────────────────────────────────────────────────

  const createNoteMutation = useMutation({
    mutationFn: (text: string) =>
      evidenceApi.createEvidence({
        area: area.id,
        type: 'note',
        note_text: text,
        folder: '',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      setNoteText('')
      setIsAddingNote(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => evidenceApi.deleteEvidence(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  const lockMutation = useMutation({
    mutationFn: async () => {
      if (isLocked) {
        return api().post(`/qa/areas/${area.id}/unlock/`, { reason: 'Reopened for editing' })
      }
      return api().post(`/qa/areas/${area.id}/lock/`, { reason: 'Area complete' })
    },
    onSuccess: () => {
      onStatusChange?.()
      queryClient.invalidateQueries({ queryKey: ['qa-floors'] })
    },
  })

  // ── File upload (two-step: upload → link evidence) ──────────────────────

  const handleFileUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setIsUploading(true)
    setUploadProgress(0)

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('purpose', 'qa_evidence')

        const uploadResponse = await api().post('/files/upload/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              setUploadProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100))
            }
          },
        })

        const isImage = file.type.startsWith('image/') || /\.hei[cf]$/i.test(file.name)
        const isVideo = file.type.startsWith('video/')

        await evidenceApi.createEvidence({
          area: area.id,
          type: isImage ? 'photo' : isVideo ? 'video' : 'document',
          asset_id: uploadResponse.data.id,
          description: file.name,
          folder: '',
        })

        setUploadProgress(Math.round(((i + 1) / files.length) * 100))
      } catch (err) {
        if (import.meta.env.DEV) console.error(`[QA AreaCard] Upload failed for "${file.name}":`, err)
      }
    }

    setIsUploading(false)
    setUploadProgress(0)
    queryClient.invalidateQueries({ queryKey })
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (cameraInputRef.current) cameraInputRef.current.value = ''
  }, [area.id, queryKey, queryClient])

  const fileListOf = (file: File) => {
    const transfer = new DataTransfer()
    transfer.items.add(file)
    return transfer.files
  }

  // ── Render ──────────────────────────────────────────────────────────────

  // WHY: Visual states make it instantly clear which areas need attention
  const borderColor = isLocked
    ? 'border-emerald-500/50 bg-emerald-50/50 dark:bg-emerald-900/10'
    : hasContent
    ? 'border-blue-500/30 bg-blue-50/30 dark:bg-blue-900/5'
    : 'border-border bg-accent/30'

  return (
    <div className={cn('rounded-xl border-2 transition-all', borderColor)}>
      {/* ── Header: checkbox + name + badge (compact on mobile) ────────── */}
      <div className="flex items-center gap-2 px-2.5 py-2 sm:px-3 sm:py-2.5">
        {/* Complete toggle — smaller on mobile */}
        <button
          onClick={() => lockMutation.mutate()}
          disabled={lockMutation.isPending}
          title={isLocked ? 'Reopen area' : 'Mark area complete'}
          className={cn(
            'w-6 h-6 sm:w-7 sm:h-7 rounded-md sm:rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all',
            isLocked
              ? 'bg-emerald-500 border-emerald-500 text-white'
              : 'border-muted-foreground/30 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-transparent hover:text-emerald-500'
          )}
        >
          {lockMutation.isPending ? <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" /> : <Check className="w-3 h-3 sm:w-4 sm:h-4" />}
        </button>

        {/* Name + code — truncated */}
        <Link to={`/areas/${area.id}/execute`} className="flex-1 min-w-0 group">
          <p className={cn(
            'font-semibold text-xs sm:text-sm leading-tight transition-colors group-hover:text-primary truncate',
            isLocked ? 'text-emerald-700 dark:text-emerald-400 line-through decoration-emerald-400/50' : 'text-foreground'
          )}>
            {area.area_name}
          </p>
          <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{area.area_code}</p>
        </Link>

        {/* Evidence badges — only show counts, compact */}
        {hasContent && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {photoCount > 0 && (
              <span className="inline-flex items-center gap-px text-[9px] sm:text-[10px] font-medium px-1 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                <Camera className="w-2.5 h-2.5" />{photoCount}
              </span>
            )}
            {noteCount > 0 && (
              <span className="inline-flex items-center gap-px text-[9px] sm:text-[10px] font-medium px-1 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                <MessageSquare className="w-2.5 h-2.5" />{noteCount}
              </span>
            )}
          </div>
        )}

        {/* Status — short text */}
        <span className={cn(
          'text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full flex-shrink-0 font-medium whitespace-nowrap',
          isLocked ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
          area.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
          'bg-muted text-muted-foreground'
        )}>
          {isLocked ? 'done' : area.status === 'in_progress' ? 'active' : area.status.replace(/_/g, ' ')}
        </span>
      </div>

      {/* ── Action row — tighter, wraps on narrow screens ─────────────── */}
      {!isLocked && (
        <div className="flex flex-wrap items-center gap-1.5 px-2.5 pb-2 sm:px-3 sm:gap-2">
          {/* Hidden file inputs */}
          <input ref={cameraInputRef} type="file" accept={QA_PHOTO_ACCEPT} capture="environment" className="hidden" onChange={(e) => handleFileUpload(e.target.files)} />
          <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf" className="hidden" onChange={(e) => handleFileUpload(e.target.files)} />

          <button
            onClick={() => cameraInputRef.current?.click()}
            disabled={isUploading}
            className="inline-flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium rounded-md sm:rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
          >
            <Camera className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            Photo
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="inline-flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium rounded-md sm:rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
          >
            <Upload className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            File
          </button>

          <AreaCardPhotoSources
            busy={isUploading}
            onPickFile={(file) => handleFileUpload(fileListOf(file))}
            onPickAsset={async (assetId, filename) => {
              setIsUploading(true)
              try {
                await evidenceApi.createEvidence({
                  area: area.id,
                  type: 'photo',
                  asset_id: assetId,
                  description: filename || 'QDrive photo',
                  folder: '',
                })
                queryClient.invalidateQueries({ queryKey })
              } finally {
                setIsUploading(false)
              }
            }}
          />

          <button
            onClick={() => { setIsAddingNote(true); setTimeout(() => noteInputRef.current?.focus(), 50) }}
            disabled={isUploading}
            className="inline-flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium rounded-md sm:rounded-lg bg-blue-500/10 text-blue-700 dark:text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
          >
            <MessageSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            Note
          </button>

          <Link
            to={`/areas/${area.id}/execute`}
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 sm:px-4 sm:py-1.5 text-[10px] sm:text-xs font-semibold rounded-md sm:rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors"
          >
            <Play className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-current" />
            Execute
          </Link>
        </div>
      )}

      {/* ── Upload progress ───────────────────────────────────────────── */}
      {isUploading && (
        <div className="mx-3 mb-2 flex items-center gap-2 py-2 px-3 rounded-lg bg-primary/5 border border-primary/20">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-primary flex-shrink-0" />
          <div className="flex-1">
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
          <span className="text-xs text-muted-foreground font-medium">{uploadProgress}%</span>
        </div>
      )}

      {/* ── Inline note input ─────────────────────────────────────────── */}
      {isAddingNote && (
        <div className="mx-3 mb-2 space-y-2">
          <textarea
            ref={noteInputRef}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Write a note..."
            rows={2}
            className="w-full text-sm p-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter to save
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && noteText.trim()) {
                createNoteMutation.mutate(noteText.trim())
              }
              // Escape to cancel
              if (e.key === 'Escape') {
                setIsAddingNote(false)
                setNoteText('')
              }
            }}
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">⌘+Enter to save · Esc to cancel</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setIsAddingNote(false); setNoteText('') }}
                className="px-2.5 py-1 text-xs rounded-lg border border-input hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (noteText.trim()) createNoteMutation.mutate(noteText.trim())
                }}
                disabled={!noteText.trim() || createNoteMutation.isPending}
                className="px-3 py-1 text-xs rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors"
              >
                {createNoteMutation.isPending ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Evidence list (expandable, auto-shown when has content) ──── */}
      {hasContent && (
        <div className="border-t border-border/50">
          <button
            onClick={() => setShowEvidence(!showEvidence)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showEvidence ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {totalCount} item{totalCount !== 1 ? 's' : ''} attached
          </button>

          {showEvidence && (
            <div className="px-3 pb-3 space-y-1.5 animate-in slide-in-from-top-1 duration-150">
              {/* Photo/video thumbnails — grid of actual images */}
              {evidence.filter((e) => e.type === 'photo' || e.type === 'video').length > 0 && (
                <div className="grid grid-cols-4 gap-1.5">
                  {evidence
                    .filter((e) => e.type === 'photo' || e.type === 'video')
                    .map((item) => (
                      <div key={item.id} className="group relative aspect-square rounded-lg overflow-hidden bg-muted">
                        {(item.display_url ?? item.asset_url) ? (
                        <img
                          src={(item.display_url ?? item.asset_url)!}
                          alt={item.description || 'Evidence photo'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground px-1 text-center">
                            Photo unavailable
                          </div>
                        )}
                        {/* Delete overlay */}
                        <button
                          onClick={() => deleteMutation.mutate(item.id)}
                          className="absolute top-0.5 right-0.5 p-1 rounded-md bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-destructive transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                </div>
              )}

              {/* Non-image evidence (notes, documents, photos without URL) */}
              {evidence
                .filter((e) => e.type === 'note' || e.type === 'document')
                .map((item) => (
                  <div
                    key={item.id}
                    className="group flex items-start gap-2 py-1.5 px-2 rounded-md hover:bg-accent/50 transition-colors"
                  >
                    {/* Type icon */}
                    {item.type === 'note' ? (
                      <MessageSquare className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                    ) : item.type === 'photo' || item.type === 'video' ? (
                      <Image className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <File className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground whitespace-pre-wrap break-words line-clamp-2">
                        {item.type === 'note' ? item.note_text : item.description || 'Attached file'}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {item.captured_by_name} · {new Date(item.captured_at).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Delete */}
                    <button
                      onClick={() => deleteMutation.mutate(item.id)}
                      className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all flex-shrink-0"
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* ── Locked overlay indicator ──────────────────────────────────── */}
      {isLocked && (
        <div className="border-t border-emerald-500/20 px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
            <Lock className="w-3 h-3" />
            Area complete — locked
          </div>
          <button
            onClick={() => lockMutation.mutate()}
            disabled={lockMutation.isPending}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
          >
            {lockMutation.isPending ? 'Unlocking...' : 'Reopen'}
          </button>
        </div>
      )}
    </div>
  )
}
